import { useChat } from "@ai-sdk/react";
import { DefaultChatTransport } from "ai";
import type { ChatMessage } from "../../chatv2/utils/messageHelpers";
import { sanitizeStreamResponse } from "../../chatv2/utils/streamChunkSanitizer";

const API_BASE_URL = (
  process.env.REACT_APP_API_URL || "https://api.autumnplatform.com"
).replace(/\/$/, "");

interface UsePricingConfigAgentChatOptions {
  selectedDate?: string;
  chatId?: string;
}

/**
 * Serialize one message into the plain-text history the agent endpoint takes.
 *
 * Two things here are load-bearing, both learned from a real incident
 * (2026-08-21, Heart of the Village Inn): across five turns the agent replied
 * "Approve above to apply" without ever calling a tool, so no approve button
 * ever existed. It was copying the shape of its own earlier turns — because the
 * history it received had been stripped down to bare text.
 *
 *  1. **Tool calls must leave a trace.** Widget parts were dropped entirely, so
 *     a turn where the agent really built a proposal looked identical to one
 *     where it only claimed to, and it had no way to learn that the tool call is
 *     what creates the button. We now emit a compact marker per widget.
 *
 *  2. **Text parts join with a blank line, not `""`.** A turn rendered as
 *     `text │ widget │ text` used to collapse into "…up from $288.Approve
 *     above to apply." — one run-on sentence with the widget silently deleted
 *     from the middle. That is a *manufactured* example of prose flowing
 *     straight into an approval promise, and the model imitated it.
 */
function serializeMessageForHistory(msg: any): string {
  const parts: any[] = Array.isArray(msg?.parts) ? msg.parts : [];
  const chunks: string[] = [];

  for (const part of parts) {
    if (part?.type === "text" && part.text?.trim()) {
      chunks.push(part.text);
      continue;
    }

    const marker = widgetHistoryMarker(part);
    if (marker) chunks.push(marker);
  }

  if (chunks.length > 0) return chunks.join("\n\n");
  return typeof msg?.content === "string" ? msg.content : "";
}

/**
 * A one-line stand-in for a widget rendered in this turn, so the transcript
 * records that a tool call produced something the user could act on.
 */
function widgetHistoryMarker(part: any): string | null {
  if (part?.type === "data-support-ticket-compose" && part.data) {
    return `[support ticket draft rendered — subject: "${part.data.subject ?? ""}"]`;
  }

  if (
    !part?.type?.startsWith?.("tool-") ||
    part.state !== "output-available" ||
    !part.output
  ) {
    return null;
  }

  try {
    const output =
      typeof part.output === "string" ? JSON.parse(part.output) : part.output;
    if (!output?.widget) return null;
    const widget =
      typeof output.widget === "string"
        ? JSON.parse(output.widget)
        : output.widget;
    if (!widget) return null;

    const kind = widget.actionType ?? widget.type ?? "proposal";
    const title = widget.title ?? widget.question ?? "";
    const dates = widget.dateRange ? ` for ${widget.dateRange}` : "";
    return `[approval card rendered — ${kind}: "${title}"${dates}]`;
  } catch {
    return null;
  }
}

export function usePricingConfigAgentChat({
  selectedDate,
  chatId = "pricing-config-agent",
}: UsePricingConfigAgentChatOptions) {
  const chatEndpoint = `${API_BASE_URL}/ai/pricing-config-agent`;

  const chatHook = useChat({
    id: chatId,
    transport: new DefaultChatTransport({
      api: chatEndpoint,
      headers: () => ({
        Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
      }),
      prepareSendMessagesRequest: ({ id, messages }) => {
        const body = {
          id,
          messages: messages.map((msg: any) => ({
            role: msg.role,
            content: serializeMessageForHistory(msg),
          })),
          selectedDate: selectedDate ?? new Date().toISOString().split("T")[0],
          clientTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        };
        return { body };
      },
      // Reconcile the backend's newer AI SDK stream with our pinned client.
      fetch: async (url: string | URL | Request, options?: RequestInit) =>
        sanitizeStreamResponse(await fetch(url, options)),
    }),
    // Log rather than swallow. The UI already shows its own banner off
    // `chatHook.error`, so this only adds the detail needed to diagnose a
    // stream failure — an empty `onError` is why the last one reached a
    // customer with nothing in the console to explain it.
    onError: (error) => {
      console.error("[pricing-config-agent] stream failed", {
        name: error.name,
        message: error.message,
        cause: error.cause,
      });
    },
  });

  return {
    messages: chatHook.messages as ChatMessage[],
    sendMessage: chatHook.sendMessage,
    status: chatHook.status,
    error: chatHook.error,
    setMessages: chatHook.setMessages,
  };
}
